import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { CheckCircle, Lock, AlertCircle } from 'lucide-react'
import api from '../../services/api'

const PRIORITY_BADGE: Record<string, string> = {
  immediate: 'badge-critical', short_term: 'badge-high', medium_term: 'badge-medium', systemic: 'badge-low',
}

const STATUS_BADGE: Record<string, string> = {
  open: 'badge-high', in_progress: 'badge-medium', overdue: 'badge-critical', closed: 'badge-low', verified: 'badge-low',
}

export default function CorrectiveActionDetail() {
  const { id } = useParams<{ id: string }>()
  const [action, setAction] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const [editing, setEditing] = useState(false)
  const [editForm, setEditForm] = useState<any>({})
  const [savingEdit, setSavingEdit] = useState(false)

  const [showComplete, setShowComplete] = useState(false)
  const [completeNotes, setCompleteNotes] = useState('')
  const [completing, setCompleting] = useState(false)

  const [verifying, setVerifying] = useState(false)

  const load = async () => {
    try {
      const { data } = await api.get(`/corrective-actions/${id}`)
      setAction(data)
    } catch { setError('Failed to load') }
    setLoading(false)
  }

  useEffect(() => { load() }, [id])

  const startEdit = () => {
    setEditForm({
      title: action.title,
      description: action.description,
      priority: action.priority,
      due_date: action.due_date?.slice(0, 10) || '',
      verification_method: action.verification_method || '',
    })
    setEditing(true)
  }

  const saveEdit = async () => {
    setSavingEdit(true)
    try {
      const { data } = await api.patch(`/corrective-actions/${id}`, editForm)
      setAction((prev: any) => ({ ...prev, ...data }))
      setEditing(false)
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to save changes')
    }
    setSavingEdit(false)
  }

  const complete = async () => {
    setCompleting(true)
    try {
      const { data } = await api.post(`/corrective-actions/${id}/complete`, { evidence_notes: completeNotes })
      setAction((prev: any) => ({ ...prev, ...data }))
      setShowComplete(false)
      setCompleteNotes('')
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed')
    }
    setCompleting(false)
  }

  const verify = async () => {
    if (!confirm('Verify this corrective action is complete and effective?')) return
    setVerifying(true)
    try {
      const { data } = await api.post(`/corrective-actions/${id}/verify`, { verification_date: new Date().toISOString().slice(0, 10) })
      setAction((prev: any) => ({ ...prev, ...data }))
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Verification failed')
    }
    setVerifying(false)
  }

  const changeStatus = async (status: string) => {
    try {
      const { data } = await api.patch(`/corrective-actions/${id}`, { status })
      setAction((prev: any) => ({ ...prev, ...data }))
    } catch {}
  }

  if (loading) return <div className="text-center py-16" style={{ color: 'var(--text-muted)' }}>Loading...</div>
  if (!action) return <div className="text-center py-16" style={{ color: 'var(--red)' }}>{error}</div>

  const isDone = action.status === 'closed' || action.status === 'verified'

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div>
        <Link to="/corrective-actions" className="text-sm" style={{ color: 'var(--accent)' }}>← Corrective Actions</Link>
        <div className="flex items-start justify-between mt-2">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: 'var(--text)' }}>{action.action_number}</h1>
            <p className="text-sm mt-0.5" style={{ color: 'var(--text-muted)' }}>{action.source_type?.replace('_', ' ')} {action.source_id ? `· ref ${action.source_id.slice(0, 8)}` : ''}</p>
          </div>
          <div className="flex gap-2">
            <span className={`badge ${PRIORITY_BADGE[action.priority] || ''} capitalize`}>{action.priority?.replace('_', ' ')}</span>
            <span className={`badge ${STATUS_BADGE[action.status] || ''} capitalize`}>{action.status?.replace('_', ' ')}</span>
          </div>
        </div>
      </div>

      {/* Details */}
      {!editing ? (
        <div className="card space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold" style={{ color: 'var(--text)' }}>Details</h2>
            {!isDone && <button onClick={startEdit} className="btn btn-secondary text-xs py-1.5">Edit</button>}
          </div>
          <div>
            <p className="text-xs font-semibold mb-1" style={{ color: 'var(--text-muted)' }}>TITLE</p>
            <p className="font-medium" style={{ color: 'var(--text)' }}>{action.title}</p>
          </div>
          <div>
            <p className="text-xs font-semibold mb-1" style={{ color: 'var(--text-muted)' }}>DESCRIPTION</p>
            <p className="text-sm whitespace-pre-wrap" style={{ color: 'var(--text)', lineHeight: 1.7 }}>{action.description}</p>
          </div>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <p className="text-xs font-semibold mb-1" style={{ color: 'var(--text-muted)' }}>OWNER</p>
              <p style={{ color: 'var(--text)' }}>{action.owner_name || '—'}</p>
            </div>
            <div>
              <p className="text-xs font-semibold mb-1" style={{ color: 'var(--text-muted)' }}>DUE DATE</p>
              <p style={{ color: action.due_date && new Date(action.due_date) < new Date() && !isDone ? 'var(--red)' : 'var(--text)' }}>
                {action.due_date ? new Date(action.due_date).toLocaleDateString('en-AU') : '—'}
              </p>
            </div>
            <div>
              <p className="text-xs font-semibold mb-1" style={{ color: 'var(--text-muted)' }}>VERIFICATION</p>
              <p style={{ color: 'var(--text)' }}>{action.verification_method || '—'}</p>
            </div>
          </div>
        </div>
      ) : (
        <div className="card space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold" style={{ color: 'var(--text)' }}>Edit Action</h2>
            <button onClick={() => setEditing(false)} className="text-xs" style={{ color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer' }}>Cancel</button>
          </div>
          <div>
            <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Title *</label>
            <input value={editForm.title} onChange={e => setEditForm((f: any) => ({ ...f, title: e.target.value }))} />
          </div>
          <div>
            <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Description *</label>
            <textarea rows={4} value={editForm.description} onChange={e => setEditForm((f: any) => ({ ...f, description: e.target.value }))} />
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Priority</label>
              <select value={editForm.priority} onChange={e => setEditForm((f: any) => ({ ...f, priority: e.target.value }))}>
                <option value="immediate">Immediate</option>
                <option value="short_term">Short Term</option>
                <option value="medium_term">Medium Term</option>
                <option value="systemic">Systemic</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Due Date</label>
              <input type="date" value={editForm.due_date} onChange={e => setEditForm((f: any) => ({ ...f, due_date: e.target.value }))} />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Verification Method</label>
              <input value={editForm.verification_method} onChange={e => setEditForm((f: any) => ({ ...f, verification_method: e.target.value }))} placeholder="How will completion be verified?" />
            </div>
          </div>
          <div className="flex gap-3">
            <button onClick={saveEdit} disabled={savingEdit} className="btn btn-primary">{savingEdit ? 'Saving...' : 'Save Changes'}</button>
            <button onClick={() => setEditing(false)} className="btn btn-secondary">Cancel</button>
          </div>
        </div>
      )}

      {/* Lifecycle actions */}
      {!isDone && (
        <div className="card space-y-4">
          <h2 className="font-semibold" style={{ color: 'var(--text)' }}>Actions</h2>

          {error && (
            <div className="flex items-start gap-2 text-sm p-3 rounded-lg" style={{ background: 'var(--red-light)', color: 'var(--red)' }}>
              <AlertCircle size={15} className="flex-shrink-0 mt-0.5" />{error}
            </div>
          )}

          <div className="flex flex-wrap gap-3">
            {action.status === 'open' && (
              <button onClick={() => changeStatus('in_progress')} className="btn btn-secondary flex items-center gap-2">
                Start Action
              </button>
            )}
            {!showComplete && (
              <button onClick={() => setShowComplete(true)} className="btn btn-primary flex items-center gap-2">
                <CheckCircle size={15} /> Mark Complete
              </button>
            )}
          </div>

          {showComplete && (
            <div className="space-y-3 pt-2" style={{ borderTop: '1px solid var(--border)' }}>
              <label className="text-sm font-medium mb-1 block" style={{ color: 'var(--text-secondary)' }}>Evidence / Completion Notes</label>
              <textarea rows={3} placeholder="Describe what was done to complete this action..." value={completeNotes} onChange={e => setCompleteNotes(e.target.value)} autoFocus />
              <div className="flex gap-3">
                <button onClick={complete} disabled={completing} className="btn btn-primary flex items-center gap-2">
                  <Lock size={14} />{completing ? 'Completing...' : 'Confirm Complete'}
                </button>
                <button onClick={() => setShowComplete(false)} className="btn btn-secondary">Cancel</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Verification */}
      {action.status === 'closed' && (
        <div className="card space-y-4">
          <h2 className="font-semibold" style={{ color: 'var(--text)' }}>Verification</h2>
          {action.evidence_notes && (
            <div>
              <p className="text-xs font-semibold mb-1" style={{ color: 'var(--text-muted)' }}>COMPLETION NOTES</p>
              <p className="text-sm whitespace-pre-wrap" style={{ color: 'var(--text)', lineHeight: 1.7 }}>{action.evidence_notes}</p>
            </div>
          )}
          <button onClick={verify} disabled={verifying} className="btn btn-success flex items-center gap-2" style={{ background: 'var(--green-light)', color: 'var(--green)' }}>
            <CheckCircle size={15} />{verifying ? 'Verifying...' : 'Verify as Effective'}
          </button>
        </div>
      )}

      {action.status === 'verified' && (
        <div className="card p-4 flex items-center gap-3" style={{ background: 'var(--green-light)', border: '1px solid var(--green)' }}>
          <CheckCircle size={20} style={{ color: 'var(--green)', flexShrink: 0 }} />
          <div>
            <p className="font-semibold text-sm" style={{ color: 'var(--green)' }}>Verified Complete</p>
            {action.verification_date && <p className="text-xs mt-0.5" style={{ color: 'var(--green)' }}>
              Verified {new Date(action.verification_date).toLocaleDateString('en-AU')}
            </p>}
          </div>
        </div>
      )}
    </div>
  )
}
